export const MEAL_REMINDER_VERSION = 1
export const MEAL_REMINDER_KIND = "meal-reminder"
export const MEAL_REMINDER_CHANNEL_ID = "gv-meal-reminders-v2"
export const MEAL_REMINDER_ID_BASE = 200_000_000
export const MEAL_REMINDER_ID_LIMIT = 201_000_000
export const MEAL_REMINDER_HORIZON_DAYS = 7

export const MEAL_REMINDER_DEFINITIONS = Object.freeze([
	{
		key: "breakfast",
		label: "Breakfast",
		defaultEnabled: false,
		defaultTime: "09:00",
		statusType: "breakfast",
		routeMealType: "breakfast",
		title: "Breakfast check-in",
		body: "Log breakfast when you have a moment.",
		description: "Only when breakfast has not been logged.",
	},
	{
		key: "lunch",
		label: "Lunch",
		defaultEnabled: false,
		defaultTime: "13:00",
		statusType: "lunch",
		routeMealType: "lunch",
		title: "Lunch check-in",
		body: "Log lunch when you have a moment.",
		description: "Only when lunch has not been logged.",
	},
	{
		key: "dinner",
		label: "Dinner",
		defaultEnabled: false,
		defaultTime: "20:00",
		statusType: "dinner",
		routeMealType: "dinner",
		title: "Dinner check-in",
		body: "Log dinner when you have a moment.",
		description: "Only when dinner has not been logged.",
	},
	{
		key: "snacks",
		label: "Snacks",
		defaultEnabled: false,
		defaultTime: "16:00",
		statusType: "snacks",
		routeMealType: "snack",
		title: "Snack check-in",
		body: "Log your snacks when you have a moment.",
		description: "Only when no snack or dessert has been logged.",
	},
	{
		key: "dailyCatchup",
		label: "End-of-day catch-up",
		defaultEnabled: true,
		defaultTime: "21:00",
		statusType: null,
		routeMealType: null,
		title: "A quick meal check-in",
		body: "Log today's meals when you have a moment.",
		description: "Only when nothing has been logged all day.",
	},
])

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/

export function createDefaultMealReminderSettings() {
	const settings = {
		version: MEAL_REMINDER_VERSION,
		introSeen: false,
	}

	for (const definition of MEAL_REMINDER_DEFINITIONS) {
		settings[definition.key] = {
			enabled: definition.defaultEnabled,
			time: definition.defaultTime,
		}
	}

	return settings
}

export function normalizeMealReminderSettings(value) {
	const defaults = createDefaultMealReminderSettings()
	if (!value || typeof value !== "object") return defaults

	const normalized = {
		version: MEAL_REMINDER_VERSION,
		introSeen: value.introSeen === true,
	}

	for (const definition of MEAL_REMINDER_DEFINITIONS) {
		const candidate = value[definition.key]
		normalized[definition.key] = {
			enabled:
				typeof candidate?.enabled === "boolean"
					? candidate.enabled
					: definition.defaultEnabled,
			time: TIME_PATTERN.test(candidate?.time ?? "")
				? candidate.time
				: definition.defaultTime,
		}
	}

	return normalized
}

export function hasEnabledMealReminders(settings) {
	const normalized = normalizeMealReminderSettings(settings)
	return MEAL_REMINDER_DEFINITIONS.some(
		(definition) => normalized[definition.key].enabled
	)
}

export function toLocalDateKey(date) {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, "0")
	const day = String(date.getDate()).padStart(2, "0")
	return `${year}-${month}-${day}`
}

export function isMealReminderNotificationId(id) {
	return (
		Number.isInteger(id) &&
		id >= MEAL_REMINDER_ID_BASE &&
		id < MEAL_REMINDER_ID_LIMIT
	)
}

export function getMealReminderNotificationId(dateKey, reminderKey) {
	const definitionIndex = MEAL_REMINDER_DEFINITIONS.findIndex(
		(definition) => definition.key === reminderKey
	)
	if (definitionIndex < 0) {
		throw new Error(`Unknown meal reminder key: ${reminderKey}`)
	}

	const [year, month, day] = dateKey.split("-").map(Number)
	const epochDay = Math.floor(Date.UTC(year, month - 1, day) / 86_400_000)
	return MEAL_REMINDER_ID_BASE + epochDay * 10 + definitionIndex
}

export function buildMealReminderSchedule({
	settings,
	now = new Date(),
	status = null,
	horizonDays = MEAL_REMINDER_HORIZON_DAYS,
}) {
	const normalized = normalizeMealReminderSettings(settings)
	const todayKey = toLocalDateKey(now)
	const loggedMealTypes =
		status?.localDate === todayKey && Array.isArray(status.loggedMealTypes)
			? new Set(status.loggedMealTypes)
			: new Set()
	const hasAnyEntry =
		status?.localDate === todayKey && status.hasAnyEntry === true
	const schedule = []

	for (let dayOffset = 0; dayOffset < horizonDays; dayOffset += 1) {
		const date = new Date(now)
		date.setHours(0, 0, 0, 0)
		date.setDate(date.getDate() + dayOffset)
		const dateKey = toLocalDateKey(date)

		for (const definition of MEAL_REMINDER_DEFINITIONS) {
			const reminder = normalized[definition.key]
			if (!reminder.enabled) continue

			if (dayOffset === 0) {
				if (definition.key === "dailyCatchup" && hasAnyEntry) continue
				if (
					definition.statusType &&
					loggedMealTypes.has(definition.statusType)
				) {
					continue
				}
			}

			const [hour, minute] = reminder.time.split(":").map(Number)
			const at = new Date(date)
			at.setHours(hour, minute, 0, 0)
			if (at <= now) continue

			schedule.push({
				id: getMealReminderNotificationId(dateKey, definition.key),
				reminderKey: definition.key,
				mealType: definition.routeMealType,
				dateKey,
				at,
				title: definition.title,
				body: definition.body,
			})
		}
	}

	return schedule
}
